<template>
  <footer
    id="contact"
    class="public-footer"
  >
    <div class="public-footer__container">
      <!-- Main Content -->
      <div class="public-footer__content">
        <!-- Organization Info -->
        <div class="public-footer__org">
          <div class="public-footer__org-name">
            {{ organizationName || t('public.footer.organization') }}
          </div>

          <div
            v-if="contactPhone || contactEmail"
            class="public-footer__contact"
          >
            <a
              v-if="contactPhone"
              :href="`tel:${contactPhone}`"
              class="public-footer__contact-link"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              {{ contactPhone }}
            </a>

            <a
              v-if="contactEmail"
              :href="`mailto:${contactEmail}`"
              class="public-footer__contact-link"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              {{ contactEmail }}
            </a>
          </div>

          <div
            v-if="address"
            class="public-footer__address"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle
                cx="12"
                cy="10"
                r="3"
              />
            </svg>
            <span>{{ address }}</span>
          </div>
        </div>

        <!-- Legal Links -->
        <nav
          class="public-footer__legal"
          aria-label="Liens légaux"
        >
          <router-link
            :to="cgvLink"
            class="public-footer__legal-link"
          >
            {{ t('public.footer.cgv') }}
          </router-link>
          <router-link
            :to="mentionsLink"
            class="public-footer__legal-link"
          >
            {{ t('public.footer.legal_mentions') }}
          </router-link>
        </nav>
      </div>

      <!-- Powered by Mylimni -->
      <div class="public-footer__bottom">
        <div class="public-footer__powered-by">
          <span>{{ t('public.footer.powered_by') }}</span>
          <a
            href="https://mylimni.fr"
            target="_blank"
            rel="noopener noreferrer"
            class="public-footer__mylimni-link"
          >
            <img
              src="/images/logo/logo-wobg.png"
              alt="Mylimni"
              class="public-footer__mylimni-logo"
            >
          </a>
        </div>
        <p class="public-footer__copyright">
          © {{ currentYear }} {{ organizationName || 'Mylimni' }}. {{ t('public.footer.rights_reserved') }}
        </p>
        <router-link
          to="/"
          class="public-footer__manager-link"
          data-tianji-event="public_footer_manager_cta"
        >
          {{ t('public.footer.manager_cta') }}
        </router-link>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
/**
 * @component PublicSiteFooter
 * @description Footer component for the public mini-site with organization info and legal links
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { RouteLocationRaw } from 'vue-router'

const props = defineProps<{
  // Absent on org-agnostic pages (e.g. the generic /lacs marketplace) — in that
  // case the links below fall back to Mylimni's own platform-wide legal pages
  // instead of building an unresolvable org-scoped route.
  orgSlug?: string
  organizationName?: string
  contactEmail?: string
  contactPhone?: string
  address?: string
}>()

const { t } = useI18n()

const cgvLink = computed<RouteLocationRaw>(() =>
  props.orgSlug
    ? { name: 'public-org-cgv', params: { orgSlug: props.orgSlug } }
    : { name: 'cgv' },
)

const mentionsLink = computed<RouteLocationRaw>(() =>
  props.orgSlug
    ? { name: 'public-org-mentions', params: { orgSlug: props.orgSlug } }
    : { name: 'mentions' },
)

const currentYear = computed(() => new Date().getFullYear())
</script>

<style scoped>
.public-footer {
  background: var(--public-neutral-900);
  padding: var(--public-space-12) 0 var(--public-space-6);
}

.public-footer__container {
  max-width: var(--public-container-max);
  margin: 0 auto;
  padding: 0 var(--public-container-px);
}

/* Content */
.public-footer__content {
  display: flex;
  flex-direction: column;
  gap: var(--public-space-8);
  padding-bottom: var(--public-space-8);
  border-bottom: 1px solid var(--public-neutral-800);
}

@media (min-width: 768px) {
  .public-footer__content {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
  }
}

/* Organization Info */
.public-footer__org {
  flex: 1;
}

.public-footer__org-name {
  font-family: var(--public-font-family);
  font-size: var(--public-text-xl);
  font-weight: var(--public-font-bold);
  color: var(--public-text-inverted);
  margin-bottom: var(--public-space-4);
}

.public-footer__contact {
  display: flex;
  flex-direction: column;
  gap: var(--public-space-3);
  margin-bottom: var(--public-space-4);
}

.public-footer__contact-link {
  display: inline-flex;
  align-items: center;
  gap: var(--public-space-2);
  font-family: var(--public-font-family);
  font-size: var(--public-text-base);
  color: var(--public-neutral-400);
  text-decoration: none;
  transition: color var(--public-transition-fast);
}

.public-footer__contact-link:hover {
  color: var(--public-primary-300);
}

.public-footer__contact-link svg {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.public-footer__address {
  display: flex;
  align-items: flex-start;
  gap: var(--public-space-2);
  font-family: var(--public-font-family);
  font-size: var(--public-text-sm);
  color: var(--public-neutral-500);
  line-height: var(--public-leading-relaxed);
}

.public-footer__address svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  margin-top: 2px;
}

/* Legal Links */
.public-footer__legal {
  display: flex;
  flex-direction: column;
  gap: var(--public-space-3);
}

@media (min-width: 768px) {
  .public-footer__legal {
    flex-direction: row;
    gap: var(--public-space-6);
  }
}

.public-footer__legal-link {
  font-family: var(--public-font-family);
  font-size: var(--public-text-sm);
  color: var(--public-neutral-400);
  text-decoration: none;
  transition: color var(--public-transition-fast);
}

.public-footer__legal-link:hover {
  color: var(--public-primary-300);
}

/* Bottom Section */
.public-footer__bottom {
  display: flex;
  flex-direction: column;
  gap: var(--public-space-4);
  padding-top: var(--public-space-6);
  align-items: center;
  text-align: center;
}

@media (min-width: 768px) {
  .public-footer__bottom {
    flex-direction: row;
    justify-content: space-between;
    text-align: left;
  }
}

.public-footer__powered-by {
  display: flex;
  align-items: center;
  gap: var(--public-space-2);
  font-family: var(--public-font-family);
  font-size: var(--public-text-xs);
  color: var(--public-neutral-500);
}

.public-footer__mylimni-link {
  display: flex;
  align-items: center;
  transition: opacity var(--public-transition-fast);
}

.public-footer__mylimni-link:hover {
  opacity: 0.8;
}

.public-footer__mylimni-logo {
  height: 24px;
  width: auto;
}

.public-footer__copyright {
  font-family: var(--public-font-family);
  font-size: var(--public-text-sm);
  color: var(--public-neutral-500);
  margin: 0;
}

.public-footer__manager-link {
  font-family: var(--public-font-family);
  font-size: var(--public-text-sm);
  font-weight: var(--public-font-semibold);
  color: var(--public-neutral-400);
  text-decoration: none;
  border: 1px solid var(--public-neutral-700);
  border-radius: var(--public-radius-lg);
  padding: var(--public-space-2) var(--public-space-4);
  transition: color var(--public-transition-fast), border-color var(--public-transition-fast);
}

.public-footer__manager-link:hover {
  color: var(--public-text-inverted);
  border-color: var(--public-primary-300);
}
</style>
